import {Injectable} from '@angular/core';
import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    MessageModel,
    MessageRecipientInterface,
    MessagingStatusesEnum,
    RecipientAndAccountInterface,
    RecipientFilterModel,
    RecipientSearchResponseInterface,
    RecipientService
} from '@spa/api';
import {
    AmsRecipientEmailSelector,
    AmsRecipientNameSelector,
    AmsRecipientSubjectIdSelector,
    AmsRecipientTenantIdSelector,
    AmsRecipientUserIdSelector,
    AmsRecipientUserRelationTypeSelector,
    AmsRecipientUserRelationTypeTxtSelector,
    FETCH_RECIPIENT_SIZE
} from 'appAssets/selectors';
import {
    AmsRecipientDataTableItems,
    getRecipientRowIdByCustomer,
    getRecipientRowIdByRecipient,
    MessageRequestCustomerIdSelector,
    MessageRequestCustomerTypeSelector,
    MessageRequestEmailAddressSelector,
    MessageRequestRecipientNameSelector
} from 'appAssets/descriptors';
import {ReportTableCheckboxClickEventInterface, ReportTableData, ReportTableDataRow} from 'appShared/components';
import {Helper} from 'appShared/classes';
import {MessageRequestErrorStatesEnum} from 'appEntries/panel/enums';
import {MessageRequestMessages} from 'appAssets/messages';
import {AlertsService} from 'appShared/services';
import {map} from 'rxjs/operators';

type FilteringType = number | string | string[] | boolean | MessagingStatusesEnum;

@Injectable({
    providedIn: 'root'
})
export class MessagingService {

    constructor(private readonly alertsService: AlertsService, private readonly recipientService: RecipientService) {
    }

    private isSelectedRecipient(rowId: string, selectedRecipients: MessageRecipientInterface[]): boolean {
        if (selectedRecipients.length === 0) {
            return false;
        }

        return selectedRecipients.findIndex(
            (recipient: MessageRecipientInterface) => rowId === getRecipientRowIdByCustomer(recipient)
        ) >= 0;
    }

    getPageContentByPageNumber(page: number, itemsPerPage: number, recipients: RecipientAndAccountInterface[]) {
        // TODO:This logic will be moved to the BE side in MSN-85
        let startIndex = 0;
        let endIndex = itemsPerPage;

        if (page > 1) {
            endIndex = page * itemsPerPage;
            startIndex = endIndex - itemsPerPage;
        }

        return recipients.slice(startIndex, endIndex);
    }

    public handleResponseInPipe(response: ApiResponse<ApiError[] | RecipientSearchResponseInterface>) {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<RecipientSearchResponseInterface>(
                response,
                MessageRequestErrorStatesEnum.GetRecipientsFailed
            );
        }

        if (!response.data) {
            return Helper.errorsHelper().createErrorResponse<RecipientSearchResponseInterface>(
                response,
                MessageRequestErrorStatesEnum.GetRecipientsFailed
            );
        }

        const data = response.data as RecipientSearchResponseInterface; 

        return Helper.createSuccessResponse<RecipientSearchResponseInterface>(data);
    }

    public createSearchParams(
        messageModel: MessageModel,
        filters: { [key: string]: FilteringType },
        editMode: boolean,
        currentPage: number,
    ): RecipientFilterModel {
        const filterModel = new RecipientFilterModel(currentPage, FETCH_RECIPIENT_SIZE);

        if (!editMode && messageModel) {
            filterModel.userIds = messageModel.recipientSet.map(recipient => Number(recipient.id));
        }

        if (!filters) {
            return filterModel;
        }

        const emailAddress = filters[MessageRequestEmailAddressSelector] as string;
        const recipientName = filters[MessageRequestRecipientNameSelector] as string;
        const customerType = filters[MessageRequestCustomerTypeSelector] as number;
        const customerId = filters[MessageRequestCustomerIdSelector] as number;

        if (emailAddress) {
            filterModel.emailAddress = emailAddress;
        }

        if (recipientName) {
            filterModel.fullName = recipientName;
        }

        if (customerType) {
            filterModel.userRelation = customerType;
        }

        if (customerId) {
            filterModel.companyIds = [customerId];
        }

        return filterModel;
    }

    public getSearchRecipientsWithAccountsObservable(filterModel: RecipientFilterModel) {
        return this.recipientService.searchRecipientsWithAccounts(filterModel)
            .pipe(
                map(
                    (response: ApiResponse<ApiError[] | RecipientSearchResponseInterface>) => this.handleResponseInPipe(response)
                )
            );
            
    }

    handleErrors(response: ApiResponse<ApiError[] | RecipientSearchResponseInterface | boolean>): void {

        if (MessagingService.isContaintAPIError(response)) {
            this.alertsService.addAlert(response.messages[0]);

            return;
        }

        (response.data as ApiError[]).map((error: ApiError) => {
            switch (error.status) {
                case MessageRequestErrorStatesEnum.CreateMessageRequestFailed:
                    return MessageRequestMessages.CreateMessageRequestFailed.message;

                case MessageRequestErrorStatesEnum.GetKeyCodesFailed:
                    return MessageRequestMessages.GetKeyCodesFailed.message;

                case  MessageRequestErrorStatesEnum.GetRecipientsFailed:
                    return MessageRequestMessages.GetRecipientsFailed.message;

                case MessageRequestErrorStatesEnum.GetRecipientsNoData:
                    return MessageRequestMessages.GetRecipientsNoData.message;
            }

            return MessageRequestMessages.UnknownError.message;
        }).forEach((errorMessage: string) => {
            this.alertsService.addAlert(errorMessage);
        });
    }

    rowCheckboxClicked(
        event: ReportTableCheckboxClickEventInterface,
    ): MessageRecipientInterface {

        const currentRecipient: MessageRecipientInterface = {
            id: null,
            email: null,
            firstName: null,
            middleName: null,
            lastName: null,
            fullName: null,
            phoneNumber: null,
            customerId: null,
            customerType: null,
            tenantId: null,
        };

        event.rowData.dataItems.forEach(colData => {

            switch (colData.colId) {

                case AmsRecipientUserIdSelector:
                    currentRecipient.id = colData.value;
                    break;

                case AmsRecipientSubjectIdSelector:
                    currentRecipient.customerId = colData.value;
                    break;

                case AmsRecipientUserRelationTypeSelector:
                    currentRecipient.customerType = colData.value;
                    break;

                case AmsRecipientNameSelector:
                    currentRecipient.fullName = colData.value;
                    break;

                case AmsRecipientEmailSelector:
                    currentRecipient.email = colData.value;
                    break;
                case AmsRecipientTenantIdSelector:
                    currentRecipient.tenantId = colData.value;
                    break;

            }
        });

        return currentRecipient;
    }

    createDataSource(data: RecipientAndAccountInterface[], selectedRecipients: MessageRecipientInterface[]): ReportTableDataRow[] {
        if (data.length !== 0) {
            return data.map((item: RecipientAndAccountInterface) => {
                const rowId = getRecipientRowIdByRecipient(item);
                const row = new ReportTableDataRow({
                    checked: this.isSelectedRecipient(rowId, selectedRecipients),
                    checkable: true,
                    rowId,
                    dataItems: [],
                    disabledActions: {},
                });

                row.dataItems = AmsRecipientDataTableItems().map((dataTableItem: ReportTableData) => {
                    switch (dataTableItem.colId) {
                        case AmsRecipientSubjectIdSelector:
                            dataTableItem.value = item.subject_id;
                            break;

                        case AmsRecipientUserIdSelector:
                            dataTableItem.value = item.user_id;
                            break;

                        case AmsRecipientUserRelationTypeTxtSelector:
                            dataTableItem.value = Helper.textsHelper().getUserRelationType(item.user_relation_type);
                            break;

                        case AmsRecipientUserRelationTypeSelector:
                            dataTableItem.value = item.user_relation_type;
                            break;

                        case AmsRecipientNameSelector:
                            dataTableItem.value = item.full_name;
                            break;

                        case AmsRecipientEmailSelector:
                            dataTableItem.value = item.email;
                            break;

                        case AmsRecipientTenantIdSelector:
                            dataTableItem.value = item.tenant_id;
                            dataTableItem.hidden = true;
                            break;
                    }

                    return dataTableItem;
                });

                return row;
            });
        }

        return [];
    }

    public static isContaintAPIError(response: ApiResponse<ApiError[] | RecipientSearchResponseInterface | boolean>) {
        return Array.isArray(response.messages) && response.messages[0];
    }

}
